import './new-relic-init.ts'
import React, { Suspense } from 'react'
import 'reflect-metadata'
import ReactDOM from 'react-dom/client'
import './global.css'
import './index.css'
import './window.d.ts'
import { createBrowserRouter, RouterProvider } from 'react-router'
import { App } from '@/common/layouts/App.tsx'
import { ErrorPage } from '@/pages/error-page.tsx'
import { IndexPage } from '@/pages/index-page.tsx'
import { Provider as ReduxProvider } from 'react-redux'
import store from '@/redux'
import { LoginPage } from '@/pages/login-page.tsx'
import { LogoutPage } from '@/pages/logout-page.tsx'
import { documentAutofillRoutes } from '@/pages/document-autofill'
import { employeeDataRoutes, employeeDataUserRoutes } from '@/pages/employee-data'
import { App as AntdApp, ConfigProvider as AntdConfigProvider, theme } from 'antd'
import { StyleProvider } from '@ant-design/cssinjs'
import advancedFormat from 'dayjs/plugin/advancedFormat'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import localeData from 'dayjs/plugin/localeData'
import weekday from 'dayjs/plugin/weekday'
import weekOfYear from 'dayjs/plugin/weekOfYear'
import weekYear from 'dayjs/plugin/weekYear'
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore'
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter'
import dayjs from 'dayjs'
import { organizationChartRoutes } from '@/pages/organization-chart'
import { headcountRoutes } from '@/pages/headcount'
import { portalConfigurationRoutes } from '@/pages/portal-configuration'
import { staffCostRoutes } from '@/pages/staff-cost'
import { appraisalRoutes, userAppraisalRoutes } from '@/pages/appraisal'
import { agGridInit } from '@/ag-grid-init.ts'
import { PrivateRoutes } from '@/common/layouts/PrivateRoutes.tsx'
import { HcAdminRoutes } from '@/common/layouts/HcAdminRoutes.tsx'
import { routes } from '@/utils/routes.ts'
import { termSheetsRoutes } from '@/pages/document-autofill/term-sheets'
import { checklistRoutes, userChecklistRoutes } from '@/pages/checklist'
import { rewardExerciseRoutes } from '@/pages/reward-exercise'
import { SuspenseLoading } from '@/common/components/SuspenseLoading.tsx'
import { timeOffRoutes, userTimeOffRoutes } from '@/pages/time-off'
import { AppEnv } from '@/utils/env.ts'
import { onboardingRoutes } from '@/pages/onboarding'
import { offboardingRoutes } from "@/pages/offboarding";
import { probationRoutes } from '@/pages/probation'
import { promotionRoutes } from '@/pages/promotion'
import { overtimeRoutes } from "@/pages/overtime";

const DocumentationPage = React.lazy(() => import('@/pages/documentation/index.tsx'))

agGridInit()

dayjs.extend(customParseFormat)
dayjs.extend(advancedFormat)
dayjs.extend(weekday)
dayjs.extend(localeData)
dayjs.extend(weekOfYear)
dayjs.extend(weekYear)
dayjs.extend(isSameOrBefore)
dayjs.extend(isSameOrAfter)

const router = createBrowserRouter([
  { path: routes.login, element: <LoginPage />, errorElement: <ErrorPage /> },
  { path: routes.logout, element: <LogoutPage />, errorElement: <ErrorPage /> },
  {
    path: 'hc-admin',
    element: <HcAdminRoutes><App /></HcAdminRoutes>,
    errorElement: <ErrorPage />,
    children: [
      documentAutofillRoutes,
      employeeDataRoutes,
      organizationChartRoutes,
      headcountRoutes,
      portalConfigurationRoutes,
      staffCostRoutes,
      appraisalRoutes,
      checklistRoutes,
      timeOffRoutes,
      ...(AppEnv.features.onboarding ? [onboardingRoutes] : []),
      ...(AppEnv.features.offboarding ? [offboardingRoutes] : []),
      { path: '*', element: <p>Page Not Found</p> },
    ],
  },
  {
    path: routes.documentation,
    element: <HcAdminRoutes>
      <Suspense fallback={<SuspenseLoading />}>
        <DocumentationPage />
      </Suspense>
    </HcAdminRoutes>,
    errorElement: <ErrorPage />,
  },
  {
    path: '',
    element: (
      <PrivateRoutes>
        <App />
      </PrivateRoutes>
    ),
    errorElement: <ErrorPage />,
    children: [
      { index: true, element: <IndexPage /> },
      userAppraisalRoutes,
      userChecklistRoutes,
      termSheetsRoutes,
      rewardExerciseRoutes,
      userTimeOffRoutes,
      ...(AppEnv.features.overtime) ? [overtimeRoutes] : [],
      ...(AppEnv.features.probation ? [probationRoutes] : []),
      ...(AppEnv.features.promotion ? [promotionRoutes] : []),
      ...(AppEnv.features.employeeProfile ? [employeeDataUserRoutes] : []),
      { path: '*', element: <p>Page Not Found</p> },
    ],
  },
])

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <StyleProvider layer={true}>
      <AntdConfigProvider theme={{ algorithm: theme.defaultAlgorithm }}>
        <AntdApp className="w-full h-full">
          <ReduxProvider store={store}>
            <RouterProvider router={router} />
          </ReduxProvider>
        </AntdApp>
      </AntdConfigProvider>
    </StyleProvider>
  </React.StrictMode>,
)
